/**
 * WantedIndicator – Fahndungslevel-Badge in der Navigation.
 * SAUBER: unsichtbar. ORDNUNGSAMT/STREIFE: gelb. KRIPO: orange, pulsierend. LKA/SEK: rot, stark pulsierend.
 * Klick öffnet ein Tooltip mit WantedPoints-Balken.
 */

import { gameState } from '../state/gameState.js';
import { formatWantedLevel, getNextWantedThreshold } from '../utils/wanted-labels.js';

const INDICATOR_ID = 'wanted-indicator';

function getBadgeClass(level: number): string {
  if (level === 0) return 'wanted-indicator--hidden';
  if (level <= 2) return 'wanted-indicator--low';
  if (level === 3) return 'wanted-indicator--mid wanted-indicator--pulse';
  return 'wanted-indicator--high wanted-indicator--pulse-strong';
}

function renderTooltip(level: number, points: number): string {
  const pct = Math.min(100, (points / 600) * 100);
  const nextThreshold = getNextWantedThreshold(level);
  return `
    <div class="wanted-tooltip">
      <div class="wanted-tooltip-label">${formatWantedLevel(level)}</div>
      <div class="wanted-tooltip-bar">
        <div class="wanted-tooltip-fill" style="width:${pct.toFixed(1)}%"></div>
      </div>
      <div class="wanted-tooltip-next">
        ${nextThreshold !== null ? `Nächste Stufe: ${String(nextThreshold)} Punkte` : 'MAXIMALE FAHNDUNG'}
      </div>
    </div>
  `;
}

export function initWantedIndicator(): void {
  if (document.getElementById(INDICATOR_ID)) return;

  const el = document.createElement('div');
  el.id = INDICATOR_ID;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  document.body.appendChild(el);

  injectStyles();
  updateWantedIndicator();

  el.addEventListener('click', (e) => {
    e.stopPropagation();
    el.classList.toggle('wanted-indicator--tooltip-open');
  });

  document.addEventListener('click', () => {
    el.classList.remove('wanted-indicator--tooltip-open');
  });

  gameState.on('player:updated', updateWantedIndicator);
}

export function updateWantedIndicator(): void {
  const el = document.getElementById(INDICATOR_ID);
  if (!el) return;

  const player = gameState.getPlayer();
  const level = player?.wantedLevel ?? 0;
  const points = player?.wantedPoints ?? 0;

  el.className = `wanted-indicator ${getBadgeClass(level)}`;
  el.innerHTML = level === 0
    ? ''
    : `
      <span class="wanted-indicator-badge">${formatWantedLevel(level)}</span>
      ${renderTooltip(level, points)}
    `;
}

function injectStyles(): void {
  const styleId = 'wanted-indicator-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .wanted-indicator {
      position: fixed;
      top: 12px;
      right: 16px;
      z-index: 900;
      cursor: pointer;
      user-select: none;
    }
    .wanted-indicator--hidden { display: none; }

    .wanted-indicator-badge {
      display: inline-block;
      font-family: var(--font-mono);
      font-size: 0.7rem;
      letter-spacing: 0.1em;
      padding: 2px 6px;
      border: 1px solid;
      font-weight: bold;
    }
    .wanted-indicator--low .wanted-indicator-badge {
      color: var(--accent-gold);
      border-color: var(--accent-gold);
      background: rgba(240,180,41,0.08);
    }
    .wanted-indicator--mid .wanted-indicator-badge {
      color: var(--accent-orange);
      border-color: var(--accent-orange);
      background: rgba(244,162,97,0.08);
    }
    .wanted-indicator--high .wanted-indicator-badge {
      color: var(--accent-red);
      border-color: var(--accent-red);
      background: rgba(230,57,70,0.08);
    }
    @keyframes nt-pulse {
      0%,100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes nt-pulse-strong {
      0%,100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.08); }
    }
    .wanted-indicator--pulse .wanted-indicator-badge {
      animation: nt-pulse 2s ease-in-out infinite;
    }
    .wanted-indicator--pulse-strong .wanted-indicator-badge {
      animation: nt-pulse-strong 1.2s ease-in-out infinite;
    }

    .wanted-tooltip {
      display: none;
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      padding: 8px 10px;
      min-width: 180px;
      font-family: var(--font-mono);
      font-size: 0.65rem;
      color: var(--text-muted);
    }
    .wanted-indicator--tooltip-open .wanted-tooltip { display: block; }
    .wanted-tooltip-label {
      color: var(--text-primary);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 6px;
    }
    .wanted-tooltip-bar {
      height: 4px;
      background: var(--border);
      margin-bottom: 4px;
    }
    .wanted-tooltip-fill {
      height: 100%;
      background: var(--accent-red);
      transition: width 0.3s;
    }
    .wanted-tooltip-next {
      font-size: 0.6rem;
      color: var(--text-muted);
    }
  `;
  document.head.appendChild(style);
}
